import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import PopUpConfirmDeleteDraft from '@/pages/profile/drafts/components/PopUpConfirmDeleteDraft';
import GalleryService from '@/services/gallery.service';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import './styles/QueuedPostsPopup.css';

const MADRID_TIMEZONE = 'Europe/Madrid';

function formatQueueDate(isoString) {
  if (!isoString) return '—';
  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) return '—';

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: MADRID_TIMEZONE
  }).format(parsed);
}

function buildPostLabel(post, t) {
  const rawTitle = post?.post_container?.post_title;
  if (rawTitle && rawTitle.trim()) {
    return rawTitle.trim();
  }

  const description = post?.post_container?.post_description;
  if (description && description.trim()) {
    return description.trim();
  }

  const firstText = post?.post_container?.post_texts?.[0]?.content ?? '';
  const stripped = firstText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (stripped) {
    const maxLength = 60;
    return stripped.length > maxLength
      ? `${stripped.slice(0, maxLength - 1)}…`
      : stripped;
  }

  const identifier = post?._id ? post._id.slice(-6) : '';
  return identifier ? t('fallback.post_identifier', { id: identifier }) : t('fallback.queued_post');
}

export default function QueuedPostsPopup({
  open = false,
  onClose,
  posts = []
}) {
  const t = useTranslations('common.profile.popups.queued_posts');
  const navigate = useNavigate();
  const { cacheHandler } = useStateHandler();
  const [pendingPost, setPendingPost] = useState(null);
  const [removedIds, setRemovedIds] = useState([]);

  const handleBackgroundClick = useCallback(event => {
    const card = event.currentTarget.querySelector('.generic-popup__card');
    if (card && card.contains(event.target)) {
      return;
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);

  const sortedPosts = useMemo(() => {
    return [...posts].sort((a, b) => {
      const aDate = Date.parse(a?.publishedAt ?? a?.post_container?.publishedAt ?? '');
      const bDate = Date.parse(b?.publishedAt ?? b?.post_container?.publishedAt ?? '');
      return aDate - bDate;
    });
  }, [posts]);

  useEffect(() => {
    if (removedIds.length === 0) return;
    const activeIds = new Set(posts.map(item => item._id));
    setRemovedIds(prev => {
      const next = prev.filter(id => activeIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [posts, removedIds]);

  const visiblePosts = useMemo(() => {
    if (removedIds.length === 0) return sortedPosts;
    const removedSet = new Set(removedIds);
    return sortedPosts.filter(item => !removedSet.has(item._id));
  }, [sortedPosts, removedIds]);

  const handleRemoveClick = useCallback((event, post) => {
    event?.stopPropagation?.();
    setPendingPost(post);
  }, []);

  const handleItemOpen = useCallback(post => {
    if (!post?._id) return;

    const destination = `/profile/edit-post/${post._id}`;

    if (typeof onClose === 'function') {
      onClose();
    }

    navigate(destination, {
      state: { postData: post }
    });
  }, [navigate, onClose]);

  const handleConfirmDelete = useCallback(async event => {
    event?.stopPropagation?.();
    if (!pendingPost) return;

    const postId = pendingPost._id;
    setRemovedIds(prev => (prev.includes(postId) ? prev : [...prev, postId]));

    try {
      await GalleryService.removePostDraft(postId);
      cacheHandler.triggerAction('DELETE_POST');
    } catch (error) {
      console.error('Failed to remove queued post', error);
      throw error;
    } finally {
      setPendingPost(null);
    }
  }, [pendingPost, cacheHandler]);

  const pendingPostTitle = pendingPost ? buildPostLabel(pendingPost, t) : t('fallback.this_post');

  return (
    <GenericPopup
      open={open}
      className="queued-posts-popup remove-scrollbar"
      darkerBlur
      opacity
      onBackgroundClick={handleBackgroundClick}
    >
      <div className="queued-posts-popup__header">
        <Text className="queued-posts-popup__title font-size-large">
          {t('title')}
        </Text>
        <XIcon className="queued-posts-popup__close" onClick={onClose} />
      </div>
      <div className="queued-posts-popup__list">
        {visiblePosts.length === 0 ? (
          <Text className="queued-posts-popup__empty font-size-small">
            {t('empty')}
          </Text>
        ) : (
          visiblePosts.map(item => {
            const scheduled = item?.publishedAt ?? item?.post_container?.publishedAt;
            const label = buildPostLabel(item, t);
            return (
              <div
                key={item._id}
                className="queued-posts-popup__item element-non-selectable"
                role="button"
                tabIndex={0}
                onClick={() => handleItemOpen(item)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleItemOpen(item);
                  }
                }}
              >
                <button
                  type="button"
                  className="queued-posts-popup__remove-button"
                  aria-label={t('aria.remove_queued_post')}
                  onClick={event => handleRemoveClick(event, item)}
                >
                  <span />
                </button>
                <Text
                  className="queued-posts-popup__item-title dots-on-overflow font-size-medium">
                  {label}
                </Text>
                <Text className="queued-posts-popup__item-date font-size-small">
                  {formatQueueDate(scheduled)}
                </Text>
              </div>
            );
          })
        )}
      </div>
      {pendingPost && (
        <PopUpConfirmDeleteDraft
          open
          close={() => setPendingPost(null)}
          draftName={pendingPostTitle}
          onConfirm={handleConfirmDelete}
          title={t('delete_dialog.title')}
        />
      )}
    </GenericPopup>
  );
}

QueuedPostsPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  posts: PropTypes.arrayOf(PropTypes.object)
};
